/* Load the below-the-fold routine builder only when it is likely to be used. */
(() => {
  const tag = document.currentScript;
  if (!tag) return;

  const existing = window.SBORoutineLoader;
  if (existing) {
    existing.observe();
    return;
  }

  const cssUrl = tag.dataset.routineCss;
  const jsUrl = tag.dataset.routineJs;
  if (!cssUrl || !jsUrl) return;

  let styles;
  let behavior;
  let ready = false;
  const selector = '.sbov3rb[data-sbo3-anchor="routine-builder"]';

  function loadStyles() {
    if (!styles) {
      styles = new Promise(resolve => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = cssUrl;
        link.onload = () => resolve(true);
        link.onerror = () => { styles = null; resolve(false); };
        document.head.appendChild(link);
      });
    }
    return styles;
  }

  function loadBehavior() {
    if (!behavior) {
      behavior = new Promise(resolve => {
        const script = document.createElement('script');
        script.src = jsUrl;
        script.async = true;
        script.onload = () => resolve(true);
        script.onerror = () => { behavior = null; resolve(false); };
        document.head.appendChild(script);
      });
    }
    return behavior;
  }

  function load() {
    return Promise.all([loadStyles(), loadBehavior()]).then(results => {
      ready = results.every(Boolean);
      return ready;
    });
  }

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          observer.disconnect();
          load();
        }
      }, { rootMargin: '800px 0px' })
    : null;

  function observe() {
    const roots = document.querySelectorAll(selector);
    if (!observer) {
      if (roots.length) load();
      return;
    }
    roots.forEach(root => {
      if (root.dataset.routineObserved) return;
      root.dataset.routineObserved = 'true';
      observer.observe(root);
    });
  }

  function routineLink(node) {
    const link = node.closest?.('a[href]');
    if (!link) return false;
    const url = new URL(link.href);
    return url.origin === location.origin &&
      url.pathname === location.pathname &&
      url.hash === '#routine-builder';
  }

  document.addEventListener('pointerover', event => {
    if (routineLink(event.target)) load();
  });
  document.addEventListener('focusin', event => {
    if (routineLink(event.target)) load();
  });
  document.addEventListener('click', event => {
    if (ready || event.defaultPrevented || event.button !== 0 ||
        event.metaKey || event.ctrlKey || event.shiftKey || event.altKey ||
        !routineLink(event.target)) return;

    event.preventDefault();
    load().then(() => {
      const anchor = document.getElementById('routine-builder');
      if (!anchor) return;
      if (location.hash === '#routine-builder') anchor.scrollIntoView();
      else location.hash = 'routine-builder';
    });
  });

  /* The builder's buttons and tabs do nothing until its behavior has
     arrived. A tap that lands first (a quick scroll on a slow connection)
     is held, the files are loaded, and the tap is replayed once the builder
     is ready. Starting up, the builder redraws each step's button, so the
     replay goes to the control now in the same place: same id, or the same
     kind of control inside the same step. */
  const held = new Set();
  function sameControl(control, step) {
    if (control.isConnected) return control;
    if (control.id) return document.getElementById(control.id);
    const kind = control.classList[0];
    return kind && step && step.isConnected ? step.querySelector(`.${kind}`) : null;
  }
  document.addEventListener('click', event => {
    if (ready) return;
    const control = event.target.closest?.(`${selector} button, ${selector} [role="tab"]`);
    if (!control || held.has(control)) return;
    held.add(control);
    const step = control.closest('.sbov3rb__step');
    load().then(ok => {
      held.delete(control);
      const target = ok && sameControl(control, step);
      if (target) target.click();
    });
  }, true);

  window.addEventListener('hashchange', () => {
    if (location.hash === '#routine-builder') load();
  });
  document.addEventListener('shopify:section:load', () => {
    observe();
    window.SBORoutineBuilder?.initialize();
  });

  window.SBORoutineLoader = { load, observe };
  observe();
  if (location.hash === '#routine-builder') load();
})();
